import React, { useCallback, JSX } from 'react';
import { ApiBookedItem, ApiBooking, ApiItemType, ApiTraveler, ApiTravelerType } from '@ibe/api';
import useTranslation from '../../Hooks/useTranslation';
import fallback from '../../../_Translations/generated/ssrs.en.t.json';
import Keys from '../../../_Translations/generated/ssrs.en.t.json.keys';

const RenderContent = (props: {
  item: ApiBookedItem;
  travelers: ApiTraveler[];
  travelerInfo: (traveler: ApiTraveler) => string;
  meetsFilterCriteria: (traveler: ApiTraveler) => boolean | undefined;
}): JSX.Element => {
  const { item, travelerInfo, travelers, meetsFilterCriteria } = props;

  return (
    <>
      {item.itemType === ApiItemType.CAR
        ? item.specialRequests?.map(ssr => (
            <div key={JSON.stringify(ssr)}>
              {`${ssr.description}${ssr.amount ? ` (${ssr.amount})` : ''}`}
            </div>
          ))
        : travelers
            .filter(traveler => meetsFilterCriteria(traveler))
            .map((traveler, idx) => (
              <div key={traveler.id}>
                {`${travelerInfo(traveler)}: ${item.specialRequests
                  ?.filter(ssr => ssr.travelerPosition === idx + 1)
                  .map(ssr => ssr.description)
                  .join(', ')}`}
              </div>
            ))}
    </>
  );
};

const Ssrs = (props: { booking: ApiBooking; bookedItem: ApiBookedItem }): JSX.Element => {
  const {
    booking: { travelers, bookedItems },
    bookedItem
  } = props;
  const { t } = useTranslation('ssrs', fallback, Keys);

  const travelerInfo = useCallback(
    (traveler: ApiTraveler): string => {
      if (traveler.salutation && traveler.firstName && traveler.lastName) {
        return `${traveler.salutation} ${traveler.firstName} ${traveler.lastName}`;
      }

      switch (traveler.type) {
        case ApiTravelerType.ADULT:
          return t(Keys.adult);
        case ApiTravelerType.CHILD:
          return t(Keys.child);
        case ApiTravelerType.INFANT:
          return t(Keys.infant);
        default:
          return t(Keys.adult);
      }
    },
    [t]
  );

  return (
    <>
      {bookedItem.specialRequests && bookedItem.priceByPersonId ? (
        <RenderContent
          item={bookedItem}
          travelers={travelers}
          travelerInfo={travelerInfo}
          meetsFilterCriteria={(traveler): boolean =>
            Object.keys(bookedItem.priceByPersonId).includes(traveler.id)
          }
        />
      ) : (
        bookedItems
          .filter(
            item =>
              (item.itemType === ApiItemType.FLIGHT || item.itemType === ApiItemType.CAR) &&
              item.specialRequests &&
              item.specialRequests.length > 0 &&
              bookedItem.id === item.relatedBookedItemId
          )
          .map(item => (
            <div className="mb-3" key={item.id}>
              <div className="font-weight-bold mb-1">
                {t(`headline${item.itemType.toLowerCase()}`)}
              </div>
              <RenderContent
                item={item}
                travelers={travelers}
                travelerInfo={travelerInfo}
                meetsFilterCriteria={(traveler): boolean | undefined => {
                  const foundItem = bookedItems.find(
                    currentItem => currentItem.relatedBookedItemId === bookedItem.id
                  );
                  return !foundItem
                    ? false
                    : Object.keys(foundItem.priceByPersonId).includes(traveler.id);
                }}
              />
            </div>
          ))
      )}
    </>
  );
};

export default Ssrs;
